import React, {useState, useEffect, useCallback} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { validateEmail } from '../../utils/validators';
import { AlertCircle, Loader } from 'lucide-react';
import logo from '../../assets/images/logo.png';

const EURO_COUNTRIES = [
    'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI',
    'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU',
    'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'
];

const getCountryFromIP = async () => {
    const cached = localStorage.getItem('country_code');
    if (cached) return cached;

    try {
        const res = await fetch('https://ipapi.co/json/');
        const data = await res.json();
        const code = data.country_code || 'US';
        localStorage.setItem('country_code', code);
        return code;
    } catch {
        return 'US';
    }
};

const getCurrencyByCountry = (countryCode) => {
    if (countryCode === 'GB') return 'GBP';
    if (countryCode === 'NG') return 'NGN';
    if (countryCode === 'MY') return 'MYR';
    if (EURO_COUNTRIES.includes(countryCode)) return 'EUR';
    return 'USD';
};
const Login = () => {

    const [countryCode, setCountryCode] = useState('US');
    const [currency, setCurrency] = useState('USD');

// ✅ RUNS ONCE
    useEffect(() => {
        const initCountry = async () => {
            const code = await getCountryFromIP();
            setCountryCode(code);
            setCurrency(getCurrencyByCountry(code));
        };

        initCountry();
    }, []);

    const navigate = useNavigate();
    const { login, register } = useAuth();

    const [formData, setFormData] = useState({ email: '', password: '' });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState('');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.email) newErrors.email = 'Email is required';
        else if (!validateEmail(formData.email)) newErrors.email = 'Invalid email address';
        if (!formData.password) newErrors.password = 'Password is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const redirectUser = (userType) => {
        if (userType === 'student') navigate('/student/dashboard');
        else if (userType === 'parent' || userType === 'teacher') navigate('/parent/dashboard');
        else if (userType === 'admin') navigate('/admin/dashboard');
    };

    // Handle email/password login
    const handleSubmit = async (e) => {
        e.preventDefault();
        setApiError('');
        if (!validate()) return;

        try {
            setLoading(true);
            const response = await login(formData.email, formData.password);
            redirectUser(response.user.user_type);
        } catch (error) {
            setApiError(error.message || 'Login failed. Please check your credentials.');
        } finally {
            setLoading(false);
        }
    };

    // Handle Google login by calling generic register endpoint
    const handleGoogleLogin = useCallback(async (response) => {
        try {
            setLoading(true);
            setApiError('');

            const token = response.credential;
            const decoded = parseJwt(token);

            const full_name = decoded?.name || '';
            const email = decoded?.email;

            const result = await register({
                full_name,
                email,
                user_type: 'parent',
                country_code: countryCode,
                currency,
                auth_provider: 'google',
                google_token: token
            });

            redirectUser('parent');
        } catch (error) {
            setApiError(error.message || 'Google login failed');
        } finally {
            setLoading(false);
        }
    }, [register, countryCode, currency]);

    // Initialize Google Sign-In button once
    useEffect(() => {
        const interval = setInterval(() => {
            if (window.google?.accounts?.id) {
                clearInterval(interval);

                const btn = document.getElementById('googleBtn');
                if (!btn) return;

                btn.innerHTML = '';

                window.google.accounts.id.initialize({
                    client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
                    callback: handleGoogleLogin
                });

                window.google.accounts.id.renderButton(btn, {
                    theme: 'outline',
                    size: 'large',
                    width: 250
                });
            }
        }, 100);

        return () => clearInterval(interval);
    }, [handleGoogleLogin]);


    // Optional: decode JWT token (simple base64 decoder)
    const parseJwt = (token) => {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            return JSON.parse(decodeURIComponent(atob(base64).split('').map(c =>
                '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
            ).join('')));
        } catch {
            return null;
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 flex items-center justify-center p-4">
            <div className="max-w-md w-full">
                <div className="text-center mb-10">
                    <Link to="/" className="inline-flex justify-center">
                        <img src={logo} alt="STEM4KIDDO" className="h-20 md:h-24 w-auto mx-auto" />
                    </Link>
                </div>

                <div className="card">
                    <h2 className="text-2xl font-bold mb-6">Login</h2>

                    {apiError && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-red-800">{apiError}</p>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                            <label className="block text-sm font-medium mb-2">Email Address</label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                className={`input-field ${errors.email ? 'border-red-500' : ''}`}
                                placeholder="your.email@example.com"
                            />
                            {errors.email && <p className="text-red-600 text-sm mt-1">{errors.email}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-2">Password</label>
                            <input
                                type="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                className={`input-field ${errors.password ? 'border-red-500' : ''}`}
                                placeholder="••••••••"
                            />
                            {errors.password && <p className="text-red-600 text-sm mt-1">{errors.password}</p>}
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="btn-primary w-full flex items-center justify-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <Loader className="w-5 h-5 animate-spin" />
                                    Logging in...
                                </>
                            ) : 'Login'}
                        </button>
                    </form>

                    {/* Google Sign-In Button */}
                    <div className="mt-6 flex justify-center">
                        <div id="googleBtn"></div>
                    </div>

                    <div className="mt-6 text-center">
                        <p className="text-gray-600">
                            Don't have an account?{' '}
                            <Link to="/register" className="text-purple-600 hover:text-purple-700 font-semibold">
                                Sign up
                            </Link>
                        </p>
                    </div>
                </div>

                <div className="mt-6 text-center">
                    <p className="text-xs text-gray-500">Powered by Fancybox LDA</p>
                </div>
            </div>
        </div>
    );
};

export default Login;
